import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TailAdminDataTable,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminProgressRow,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";
import { toast } from "sonner";
import GuidedTour from "@/components/campaign/GuidedTour";
import { clientAPI, productAPI } from "@/services/api";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowUpRight,
  Clock,
  CheckCircle2,
  Package,
  Plus,
  RefreshCw,
  Target,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
  Zap,
} from "lucide-react";

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [chartData, setChartData] = useState([]);
  const [enhancedAnalytics, setEnhancedAnalytics] = useState(null);
  const [growthRoi, setGrowthRoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [loadError, setLoadError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setStats(null);
    setChartData([]);
    setEnhancedAnalytics(null);
    setGrowthRoi(null);
    try {
      const productsRes = await productAPI.getProducts();
      const productList = productsRes.data || [];
      setProducts(productList);

      if (productList.length === 0) {
        setNeedsOnboarding(true);
        setLoading(false);
        return;
      }

      setNeedsOnboarding(false);
      const productFilter = selectedProduct !== "all" ? selectedProduct : null;
      const [dashboardRes, profileRes, analyticsRes, enhancedRes, roiRes] = await Promise.all([
        clientAPI.getDashboard(productFilter),
        clientAPI.getProfile(),
        clientAPI.getSubscriptionAnalytics(productFilter),
        clientAPI.getEnhancedAnalytics(productFilter).catch(() => ({ data: null })),
        clientAPI.getGrowthROI(productFilter).catch(() => ({ data: null })),
      ]);
      setStats(dashboardRes.data);
      setProfile(profileRes.data);
      setChartData(analyticsRes.data || []);
      setEnhancedAnalytics(enhancedRes.data);
      setGrowthRoi(roiRes.data);
    } catch (error) {
      const message = error.response?.data?.detail || "We couldn't load your dashboard. Your data is safe; check your connection and try again.";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const selectedProductName = useMemo(() => {
    if (selectedProduct === "all") return "Global Performance";
    return products.find((product) => getProductId(product) === selectedProduct)?.name || "Selected Product";
  }, [products, selectedProduct]);

  const funnel = enhancedAnalytics?.enrollment_funnel;
  const funnelData = funnel
    ? [
        { name: "Total", value: funnel.total || 0, tone: "primary" },
        { name: "Pending", value: funnel.pending || 0, tone: "warning" },
        { name: "Active", value: funnel.active || 0, tone: "success" },
        { name: "Not verified", value: funnel.rejected ?? funnel.churned ?? 0, tone: "danger" },
      ]
    : [];

  const topBrms = enhancedAnalytics?.top_brms || [];
  const revenueTrends = enhancedAnalytics?.revenue_trends || [];

  const statCards = [
    {
      label: "Agents with enrollments",
      value: stats?.total_brms || 0,
      icon: Users,
      tone: "primary",
    },
    {
      label: "Verified subscriptions",
      value: stats?.active_users || 0,
      icon: UserCheck,
      tone: "success",
    },
    {
      label: "Awaiting verification",
      value: stats?.pending_users || 0,
      icon: Clock,
      tone: "warning",
    },
    {
      label: "Agent rewards reserved",
      value: formatCurrency(stats?.total_commission_liability || 0),
      icon: Wallet,
      tone: "danger",
    },
  ];

  const brmRows = topBrms.slice(0, 5).map((brm, index) => ({
    id: brm.brm_id || index,
    cells: [
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700">
          {index + 1}
        </div>
        <div>
          <p className="font-medium text-gray-900">{brm.brm_name}</p>
          <p className="text-xs text-gray-500">{brm.active_count || 0} verified subscriptions</p>
        </div>
      </div>,
      formatCurrency(brm.total_commission),
      <TailAdminStatusBadge tone={index === 0 ? "success" : "neutral"}>
        {index === 0 ? "Top performer" : "Active"}
      </TailAdminStatusBadge>,
    ],
  }));

  const productRows = products.slice(0, 5).map((product) => ({
    id: getProductId(product),
    cells: [
      <div>
        <p className="font-medium text-gray-900">{product.name}</p>
        <p className="text-xs text-gray-500">{getProductId(product)}</p>
      </div>,
      formatProductCommission(product, formatCurrency),
      <TailAdminStatusBadge tone={getProductStatus(product) === "active" ? "success" : "danger"}>
        {getProductStatus(product) === "active" ? "Active" : "Paused"}
      </TailAdminStatusBadge>,
    ],
  }));

  if (!loading && needsOnboarding) {
    return (
      <div className="space-y-6 pb-10">
        <TailAdminPageHeader
          eyebrow="Proovam setup"
          title="Set up your first campaign"
          description="Create your offer, configure verification, and fund Agent rewards so Agents can start promoting."
          actions={<div className="flex flex-wrap gap-2">
            <GuidedTour accountId={localStorage.getItem('user_id')} tourId="dashboard" steps={[
              { title: 'Start with your offer', body: 'Create a Project and an offer for the subscription you want Agents to introduce.', anchor: 'dashboard-start' },
              { title: 'What makes it ready?', body: 'Add subscription confirmation, set the Agent reward, and reserve the campaign budget before publishing.', anchor: 'dashboard-checklist' },
            ]} />
            <span data-tour-id="dashboard-start"><Button className="h-11 rounded-lg px-5 font-semibold shadow-theme-xs" onClick={() => navigate("/client/onboarding")}>
              Start onboarding
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button></span>
          </div>}
        />

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs xl:col-span-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                  <Package className="h-8 w-8" />
                </div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-500">Offer launch workflow</p>
                <h2 className="text-3xl font-semibold tracking-tight text-gray-950">Turn a product into an Agent-ready campaign.</h2>
                <p className="mt-3 max-w-xl text-sm leading-6 text-gray-500">
                  Proovam needs one configured campaign before analytics, enrollment tables, webhook logs, and campaign integration become active.
                </p>
              </div>

              <div className="grid min-w-[220px] grid-cols-2 gap-3 lg:grid-cols-1">
                {[
                  ["0", "Offers"],
                  ["0", "Agents active"],
                  ["0", "Enrollments"],
                ].map(([value, label]) => (
                  <div key={label} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <p className="text-2xl font-semibold text-gray-900">{value}</p>
                    <p className="mt-1 text-xs font-medium uppercase text-gray-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-3">
              {[
                ["Create offer", "Name the product and define the reward.", CheckCircle2],
                ["Verification rules", "Choose the API or webhook proof flow.", Target],
                ["Fund Agent rewards", "Make Agent reward payouts ready.", Wallet],
              ].map(([title, copy, Icon]) => (
                <div key={title} className="rounded-2xl border border-gray-100 bg-white p-4 shadow-theme-xs">
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">{copy}</p>
                </div>
              ))}
            </div>
          </section>

          <TailAdminPanel className="xl:col-span-4" title="Setup checklist" description="What to complete before Agents can promote your campaign." action={null}>
            <span data-tour-id="dashboard-checklist" />
            <div className="space-y-3">
              <TailAdminProgressRow label="Offer details" value={0} helper="Product name, subscription, commission" tone="primary" />
              <TailAdminProgressRow label="Verification" value={0} helper="API endpoint and proof event" tone="warning" />
              <TailAdminProgressRow label="Reward funding" value={0} helper="Budget ready for Agent payouts" tone="success" />
            </div>
            <Button variant="outline" className="mt-5 h-11 w-full rounded-lg border-gray-200 bg-white" onClick={() => navigate("/client/prove-requests/create")}>
              Create manually
              <Plus className="ml-2 h-4 w-4" />
            </Button>
          </TailAdminPanel>
        </div>
      </div>
    );
  }

  if (loadError && !loading) {
    return (
      <div className="pb-10">
        <div role="alert" className="flex flex-col gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">Dashboard unavailable</p>
            <p className="mt-1 text-sm text-rose-800">{loadError}</p>
          </div>
          <Button variant="outline" className="h-11 shrink-0 border-rose-300 bg-white" onClick={fetchData}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-10">
      <TailAdminPageHeader
        eyebrow="Founder workspace"
        title={`${profile?.name || "Founder"} dashboard`}
        description={`Verified subscription results, Agent activity, campaign readiness, and rewards for ${selectedProductName.toLowerCase()}.`}
        actions={
          <>
            <GuidedTour accountId={profile?.client_id || profile?.id || localStorage.getItem('user_id')} tourId="dashboard" steps={[
              { title: 'Your campaign workspace', body: products.some((product) => getProductStatus(product) !== 'active') ? 'Some offers need attention. Open one to check its campaign setup, connection, and budget.' : 'Open an offer to review its campaign. An offer can exist before its campaign is live.', anchor: 'dashboard-new-campaign' },
              { title: growthRoi ? 'Verified subscriptions' : 'Activity at a glance', body: growthRoi ? 'Verified conversions are confirmed customer results. A form submission or link click alone is not a paid subscription.' : 'These counters show network activity. Verified subscription results appear after your campaign starts receiving confirmed customers.', anchor: growthRoi ? 'dashboard-verified' : 'dashboard-metrics' },
            ]} />
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger className="h-11 w-60 rounded-lg border-gray-200 bg-white shadow-sm">
                <Package className="mr-2 h-4 w-4 text-brand-500" />
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Global Performance</SelectItem>
                {products.map((product) => (
                  <SelectItem key={getProductId(product)} value={getProductId(product)}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="h-11 rounded-lg border-gray-200 bg-white shadow-sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <span data-tour-id="dashboard-new-campaign"><Button className="h-11 rounded-lg shadow-sm" onClick={() => navigate("/client/prove-requests/create")}>
              <Plus className="mr-2 h-4 w-4" />
              New campaign
            </Button></span>
          </>
        }
      />

      <div data-tour-id="dashboard-metrics" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {statCards.map((card) => (
          <TailAdminMetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
            tone={card.tone}
            loading={loading}
          />
        ))}
      </div>

      {growthRoi ? (
        <div data-tour-id="dashboard-verified" className="mt-6 hidden grid-cols-3 gap-4 sm:grid">
          <TailAdminMetricCard
            label="Cost per verified customer"
            value={formatCurrency(growthRoi.customer_acquisition_cost_ngn)}
            icon={Target}
            tone="primary"
            loading={loading}
          />
          <TailAdminMetricCard
            label="Agent rewards paid"
            value={formatCurrency(growthRoi.commissions_paid_ngn)}
            icon={Wallet}
            tone="warning"
            loading={loading}
          />
          <TailAdminMetricCard
            label="Reward budget left"
            value={formatCurrency(growthRoi.escrow_remaining_ngn)}
            icon={TrendingUp}
            tone="neutral"
            loading={loading}
          />
        </div>
      ) : null}

      {growthRoi?.recommendations?.length > 0 ? (
        <TailAdminPanel
          className="mt-6"
          title="Growth recommendations"
          description="Outcome-based actions — not vanity metrics."
        >
          <div className="space-y-2">
            {growthRoi.recommendations.map((rec) => (
              <div
                key={rec.action}
                className="flex items-start justify-between rounded-xl border border-gray-200 px-4 py-3"
              >
                <p className="text-sm text-gray-700">{rec.message}</p>
                <TailAdminStatusBadge tone={rec.priority === "high" ? "danger" : "warning"}>
                  {rec.priority}
                </TailAdminStatusBadge>
              </div>
            ))}
          </div>
        </TailAdminPanel>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
        <TailAdminPanel
          className="xl:col-span-8"
          title="Verified subscriptions"
          description="Confirmed subscriptions for the selected offer scope."
          action={
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-600">
              <TrendingUp className="h-4 w-4" />
              +{stats?.this_month_subscriptions || 0} this month
            </span>
          }
        >
          {loading ? (
            <Skeleton className="h-[320px] w-full" />
          ) : (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="proovamSubs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c4dff" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#7c4dff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11, fontWeight: 600 }} />
                  <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 10px 25px rgb(15 23 42 / 0.08)" }} />
                  <Area
                    type="monotone"
                    dataKey="subscriptions"
                    stroke="#7c4dff"
                    strokeWidth={3}
                    fill="url(#proovamSubs)"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </TailAdminPanel>

        <details className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs md:hidden">
          <summary className="cursor-pointer font-semibold text-gray-900">More performance details</summary>
          <p className="mt-2 text-sm leading-6 text-gray-500">Open verification, reward, and Agent summaries for this offer scope.</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Verification rate</dt><dd className="mt-1 font-semibold text-gray-900">{funnel ? `${funnel.conversion_rate || 0}%` : "Awaiting data"}</dd></div>
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Not verified</dt><dd className="mt-1 font-semibold text-gray-900">{funnel ? `${funnel.rejected_rate ?? funnel.churn_rate ?? 0}%` : "Awaiting data"}</dd></div>
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Cost per verified customer</dt><dd className="mt-1 font-semibold text-gray-900">{growthRoi ? formatCurrency(growthRoi.customer_acquisition_cost_ngn) : "Awaiting data"}</dd></div>
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Agent rewards paid</dt><dd className="mt-1 font-semibold text-gray-900">{growthRoi ? formatCurrency(growthRoi.commissions_paid_ngn) : "Awaiting data"}</dd></div>
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Reward budget left</dt><dd className="mt-1 font-semibold text-gray-900">{growthRoi ? formatCurrency(growthRoi.escrow_remaining_ngn) : "Awaiting data"}</dd></div>
            <div className="rounded-xl bg-gray-50 p-3"><dt className="text-gray-500">Top Agent</dt><dd className="mt-1 truncate font-semibold text-gray-900">{enhancedAnalytics ? topBrms[0]?.brm_name || "No activity yet" : "Awaiting data"}</dd></div>
          </dl>
        </details>

        <TailAdminPanel className="hidden md:block xl:col-span-4" title="Subscription checks" description="How enrolled customers are being verified.">
          <div className="rounded-2xl bg-gray-950 p-5 text-white">
            <Zap className="mb-4 h-8 w-8 text-brand-500" />
            <h3 className="text-lg font-semibold">
              {funnel?.conversion_rate != null ? `${funnel.conversion_rate}% conversion` : "Awaiting data"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-white/55">
              {funnel?.total
                ? `${funnel.total} enrollments tracked across your offers.`
                : "Metrics appear once Agents enroll users and verification runs."}
            </p>
          </div>
          <div className="mt-4">
            <TailAdminProgressRow label="Verification rate" value={funnel?.conversion_rate || 0} helper="Verified subscriptions out of all enrollments" tone="success" />
          </div>
          <div className="mt-3">
            <TailAdminProgressRow label="Not verified" value={funnel?.rejected_rate ?? funnel?.churn_rate ?? 0} helper="Enrollments rejected by verification" tone="danger" />
          </div>
        </TailAdminPanel>

        <TailAdminPanel className="hidden md:block xl:col-span-5" title="Customer verification" description="Current status of enrolled customers.">
          {loading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : funnel ? (
            <div className="space-y-3">
              {funnelData.map((item) => {
                const max = Math.max(funnel.total || 1, 1);
                const percent = Math.round(((item.value || 0) / max) * 100);
                return (
                  <div key={item.name} className="rounded-xl border border-gray-100 px-3 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{item.name}</span>
                      <TailAdminStatusBadge tone={item.tone}>{item.value}</TailAdminStatusBadge>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="rounded-xl bg-emerald-50 p-3 text-center">
                  <p className="text-xl font-semibold text-emerald-600">{funnel.conversion_rate || 0}%</p>
                  <p className="text-xs text-emerald-700/70">Conversion</p>
                </div>
                <div className="rounded-xl bg-rose-50 p-3 text-center">
                  <p className="text-xl font-semibold text-rose-600">{funnel.rejected_rate ?? funnel.churn_rate ?? 0}%</p>
                  <p className="text-xs text-rose-700/70">Not verified</p>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState title="No funnel data yet" description="Enhanced analytics will appear after enrollments begin." />
          )}
        </TailAdminPanel>

        <TailAdminPanel className="hidden md:block xl:col-span-7" title="Agent reward trends" description="Last 12 months of paid Agent rewards. This is not customer revenue.">
          {loading ? (
            <Skeleton className="h-[280px] w-full" />
          ) : revenueTrends.length ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueTrends}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="revenue" fill="#7c4dff" radius={[8, 8, 0, 0]} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState title="No reward trend yet" description="Paid Agent reward history will appear after verified subscriptions are rewarded." />
          )}
        </TailAdminPanel>

        <TailAdminPanel className="hidden md:block xl:col-span-7" title="Top Agent performance" description="Verified subscriptions and paid rewards by Agent.">
          {brmRows.length ? (
            <TailAdminDataTable columns={["Agent", "Rewards paid", "Status"]} rows={brmRows} loading={loading} />
          ) : (
            <EmptyState title="No Agent performance yet" description="Launch a campaign and invite Agents to see verified results here." />
          )}
        </TailAdminPanel>

        <TailAdminPanel
          className="xl:col-span-5"
          title="Offers"
          description="Subscription offers available for founder campaigns."
          action={
            <Button variant="outline" className="h-9 rounded-lg" onClick={() => navigate("/client/products")}>
              Manage
            </Button>
          }
        >
          {productRows.length ? (
            <TailAdminDataTable columns={["Offer", "Agent reward", "Status"]} rows={productRows} loading={loading} />
          ) : (
            <EmptyState title="No offers yet" description="Create your first offer to start a campaign." />
          )}
        </TailAdminPanel>
      </div>
    </div>
  );
};

function EmptyState({ title, description }) {
  return (
    <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 text-center">
      <Target className="mb-3 h-7 w-7 text-gray-400" />
      <p className="font-semibold text-gray-800">{title}</p>
      <p className="mt-1 max-w-sm text-sm leading-6 text-gray-500">{description}</p>
    </div>
  );
}

function getProductId(product) {
  return product?.productId || product?.id || product?._id || "";
}

function getProductStatus(product) {
  if (product?.isActive === false) return "paused";
  if (product?.status) return product.status;
  return "active";
}

function formatProductCommission(product, formatCurrency) {
  if (product?.commission) return formatCurrency(product.commission);
  if (product?.commission_rate && product?.subscription_amount) {
    return `${Math.round(product.commission_rate * 100)}% of ${formatCurrency(product.subscription_amount)}`;
  }
  if (product?.commission_rate) return `${Math.round(product.commission_rate * 100)}%`;
  return "Not set";
}

export default ClientDashboard;
